import React, { useState, useEffect } from 'react';
import { Store, Package, Receipt, Send, AlertCircle, Plus, Trash2, Edit, Check } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const Vendor = () => {
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);
  const load = async () => {
    try {
      const r = await api.get('/tsb/vendor/me');
      setVendor(r.data?.vendor || null);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  if (loading) return <div className="min-h-screen bg-slate-950 text-white pt-32 px-6">Loading...</div>;

  return (
    <div className="min-h-screen bg-slate-950 text-white pt-28 md:pt-32 pb-16 px-4 md:px-6" data-testid="vendor-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Store className="w-7 h-7 text-amber-400" />
          <div>
            <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-amber-300 to-orange-300 bg-clip-text text-transparent">
              TSB Vendor Portal
            </h1>
            <p className="text-xs text-slate-400">Manage your storefront on the TSB Marketplace</p>
          </div>
        </div>

        {!vendor && <RegisterForm onDone={load} />}
        {vendor?.status === 'pending' && <PendingNotice vendor={vendor} />}
        {vendor?.status === 'rejected' && <RejectedNotice vendor={vendor} onReapply={() => setVendor(null)} />}
        {vendor?.status === 'suspended' && <SuspendedNotice vendor={vendor} />}
        {vendor?.status === 'approved' && <VendorDashboard vendor={vendor} />}
      </div>
    </div>
  );
};

const RegisterForm = ({ onDone }) => {
  const [form, setForm] = useState({ business_name: '', contact_email: '', contact_phone: '', gst: '', address: '', description: '' });
  const submit = async () => {
    if (!form.business_name) return toast.error('Business name is required');
    try {
      await api.post('/tsb/vendor/register', form);
      toast.success('Application submitted! Wait for admin approval.');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="bg-slate-800/40 border border-slate-700 rounded-2xl p-6 max-w-2xl" data-testid="vendor-register-form">
      <h2 className="text-xl font-bold mb-2">Become a Vendor</h2>
      <p className="text-xs text-slate-400 mb-4">Apply to list your products on the TSB Marketplace. Admin approval required.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input placeholder="Business / Store Name *" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="vendor-business-name" className="px-3 py-2 bg-slate-900 border border-slate-700 rounded" />
        <input placeholder="Contact Email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="px-3 py-2 bg-slate-900 border border-slate-700 rounded" />
        <input placeholder="Contact Phone" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="px-3 py-2 bg-slate-900 border border-slate-700 rounded" />
        <input placeholder="GST Number (optional)" value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} className="px-3 py-2 bg-slate-900 border border-slate-700 rounded" />
      </div>
      <input placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded mt-3" />
      <textarea placeholder="Brief Business Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded mt-3" />
      <button onClick={submit} data-testid="vendor-register-submit" className="mt-4 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-black font-bold rounded-lg">
        Submit Application
      </button>
    </div>
  );
};

const PendingNotice = ({ vendor }) => (
  <div className="bg-yellow-500/10 border border-yellow-500/40 rounded-2xl p-6">
    <div className="flex items-center gap-2 text-yellow-300 font-bold"><AlertCircle className="w-5 h-5" /> Application Pending</div>
    <p className="text-sm text-slate-300 mt-2">आपका application <strong>{vendor.business_name}</strong> के लिए submit हो गया है। Admin approval का इंतज़ार करें।</p>
    <div className="text-xs text-slate-400 mt-2">Submitted: {new Date(vendor.created_at).toLocaleString()}</div>
  </div>
);

const RejectedNotice = ({ vendor, onReapply }) => (
  <div className="bg-red-500/10 border border-red-500/40 rounded-2xl p-6">
    <div className="text-red-300 font-bold">Application Rejected</div>
    <p className="text-sm mt-2">{vendor.admin_note || 'Reason not specified'}</p>
    <button onClick={onReapply} className="mt-3 px-4 py-2 bg-amber-500 text-black font-bold rounded">Re-apply</button>
  </div>
);

const SuspendedNotice = ({ vendor }) => (
  <div className="bg-orange-500/10 border border-orange-500/40 rounded-2xl p-6">
    <div className="text-orange-300 font-bold">Vendor Account Suspended</div>
    <p className="text-sm mt-2">{vendor.admin_note || 'Contact admin for details.'}</p>
  </div>
);

const VendorDashboard = ({ vendor }) => {
  const [tab, setTab] = useState('products');
  return (
    <>
      <div className="bg-gradient-to-br from-amber-500/10 via-slate-900/60 to-emerald-500/10 border border-amber-500/30 rounded-2xl p-4 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-xs text-slate-400">Vendor Storefront</div>
            <div className="text-xl font-bold text-amber-300">{vendor.business_name}</div>
            {vendor.gst && <div className="text-xs text-slate-500 mt-1">GST: {vendor.gst}</div>}
          </div>
          <span className="text-xs bg-emerald-500/20 text-emerald-300 px-3 py-1 rounded-full flex items-center gap-1"><Check className="w-3 h-3" /> APPROVED</span>
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-800 mb-4">
        {[
          { id: 'products', label: 'Products', icon: Package },
          { id: 'orders',   label: 'Orders',   icon: Receipt },
        ].map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`vendor-tab-${t.id}`} className={`px-4 py-2 flex items-center gap-2 text-sm font-medium border-b-2 ${tab === t.id ? 'border-amber-400 text-amber-300' : 'border-transparent text-slate-400'}`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'products' && <ProductsTab />}
      {tab === 'orders' && <OrdersTab />}
    </>
  );
};

const ProductsTab = () => {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ product_name: '', price_inr: '', stock: 100, category: '', description: '', image_url: '' });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => { load(); }, []);
  const load = async () => {
    const r = await api.get('/tsb/vendor/products');
    setProducts(r.data?.products || []);
  };

  const reset = () => {
    setForm({ product_name: '', price_inr: '', stock: 100, category: '', description: '', image_url: '' });
    setEditingId(null);
  };

  const save = async () => {
    if (!form.product_name || !form.price_inr) return toast.error('Name + Price required');
    const payload = { ...form, price_inr: parseFloat(form.price_inr), stock: parseInt(form.stock) || 100 };
    try {
      if (editingId) {
        await api.put(`/tsb/vendor/products/${editingId}`, payload);
        toast.success('Product updated');
      } else {
        await api.post('/tsb/vendor/products', payload);
        toast.success('Product created');
      }
      reset();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const edit = (p) => {
    setForm({ product_name: p.product_name, price_inr: p.price_inr, stock: p.stock, category: p.category || '', description: p.description || '', image_url: p.image_url || '' });
    setEditingId(p.id);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try { await api.delete(`/tsb/vendor/products/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1 bg-slate-800/40 border border-slate-700 rounded-2xl p-5 h-fit" data-testid="vendor-product-form">
        <h3 className="text-base font-bold mb-3">{editingId ? 'Edit Product' : 'Add New Product'}</h3>
        <input placeholder="Product Name *" value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} data-testid="vendor-prod-name" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded mb-2" />
        <input type="number" step="0.01" placeholder="Price (INR) *" value={form.price_inr} onChange={(e) => setForm({ ...form, price_inr: e.target.value })} data-testid="vendor-prod-price" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded mb-2" />
        <input type="number" placeholder="Stock" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded mb-2" />
        <input placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded mb-2" />
        <input placeholder="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded mb-2" />
        <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded mb-2" />
        <div className="flex gap-2">
          <button onClick={save} data-testid="vendor-prod-save" className="flex-1 py-2 bg-amber-500 text-black font-bold rounded flex items-center justify-center gap-1">
            {editingId ? <><Check className="w-4 h-4" /> Update</> : <><Plus className="w-4 h-4" /> Add</>}
          </button>
          {editingId && <button onClick={reset} className="px-3 py-2 bg-slate-600 rounded">Cancel</button>}
        </div>
      </div>

      <div className="lg:col-span-2 space-y-3" data-testid="vendor-products-list">
        {products.map((p) => (
          <div key={p.id} className="bg-slate-800/40 border border-slate-700 rounded-lg p-4 flex justify-between items-start">
            <div className="flex-1">
              <div className="font-bold">{p.product_name}</div>
              <div className="text-amber-300 font-bold text-lg">₹{p.price_inr.toLocaleString()}</div>
              <div className="text-xs text-slate-400">Stock: {p.stock} • {p.category || '—'} • {p.is_active ? 'ON' : 'OFF'}</div>
              {p.description && <div className="text-xs text-slate-500 mt-1">{p.description.slice(0, 100)}</div>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => edit(p)} className="p-2 bg-slate-700 rounded hover:bg-slate-600" title="Edit"><Edit className="w-4 h-4" /></button>
              <button onClick={() => remove(p.id)} className="p-2 bg-red-600/30 rounded hover:bg-red-600" title="Delete"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {products.length === 0 && <div className="text-slate-500 text-sm">No products yet. Add your first product from the form on the left.</div>}
      </div>
    </div>
  );
};

const OrdersTab = () => {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get('/tsb/vendor/orders').then(r => setOrders(r.data?.orders || [])); }, []);
  const totalRevenue = orders.reduce((s, o) => s + (o.total_inr || 0), 0);
  return (
    <div data-testid="vendor-orders">
      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 mb-4">
        <div className="text-xs text-emerald-300">Total Revenue</div>
        <div className="text-2xl font-bold text-emerald-200">₹{totalRevenue.toLocaleString()}</div>
        <div className="text-xs text-slate-400">{orders.length} orders</div>
      </div>
      <div className="space-y-2">
        {orders.map((o) => (
          <div key={o.id} className="bg-slate-800/40 border border-slate-700 rounded p-3 text-sm flex justify-between">
            <div>
              <div className="font-semibold">{o.product_name} × {o.quantity}</div>
              <div className="text-xs text-slate-500">{new Date(o.created_at).toLocaleString()}</div>
            </div>
            <div className="text-right">
              <div className="font-bold text-amber-300">₹{o.total_inr}</div>
              <div className="text-xs text-slate-400">₹{o.cash_paid} + {o.tsb_paid} TSB</div>
            </div>
          </div>
        ))}
        {orders.length === 0 && <div className="text-slate-500 text-sm text-center py-8">No orders yet</div>}
      </div>
    </div>
  );
};

export default Vendor;
